import { Alert, Center, Container, Loader } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import type { UserRole } from '../api/types';
import { useCurrentUser } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import type { PermissionKey } from '../api/permissionKeys';

/** The one house wording for "you can't see this page" (#4, §3h). */
const DEFAULT_DENY_MESSAGE = 'This area is available to administrators only.';

/**
 * AccessDeniedAlert — the single uniform access-denied message + markup.
 *
 * Use it directly in a page's own early-return when the page already computes
 * its gate (so we don't restructure a working page's hooks), or let
 * RequireAccess render it. Either way every admin/compliance page shows the
 * SAME message (§3h — uniform message, not necessarily a uniform gate).
 */
export function AccessDeniedAlert({ message }: { message?: string }) {
  return (
    <Container size="xl">
      <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
        {message ?? DEFAULT_DENY_MESSAGE}
      </Alert>
    </Container>
  );
}

/**
 * RequireAccess — uniform in-page access gate (#4, §3h).
 *
 * Wrap a page body so a user who lacks access sees the consistent friendly
 * message instead of a broken/empty page on direct-URL navigation. The
 * backend remains the real gate (every protected endpoint 403s); this only
 * decides what the page renders.
 *
 * Match each page to its EXISTING backend dependency — not a blanket role:
 *   - role-gated endpoint (e.g. AdminUser)  → <RequireAccess roles={['admin']}>
 *   - permission-gated endpoint             → <RequireAccess permission="user.manage_permissions">
 * Pass exactly one of `roles` / `permission`. `permission` takes precedence
 * if both are given (it is the finer gate).
 *
 * Deny-by-default: while identity / permissions load we show a loader, and
 * access stays denied until proven (mirrors useCurrentUser / usePermissions,
 * so a slow or failed load never flashes protected content).
 *
 * The children are only mounted once access is granted, so a body's data
 * hooks never fire a doomed 403 query for a user who can't see the page.
 */
export function RequireAccess({
  roles,
  permission,
  message,
  children,
}: {
  roles?: UserRole[];
  permission?: PermissionKey;
  message?: string;
  children: ReactNode;
}) {
  const { user, isLoading: userLoading } = useCurrentUser();
  const { can, isLoading: permsLoading } = usePermissions();

  const isLoading = userLoading || (permission != null && permsLoading);
  if (isLoading) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    );
  }

  const allowed =
    permission != null
      ? can(permission)
      : roles != null && user != null && roles.includes(user.role);

  if (!allowed) {
    return <AccessDeniedAlert message={message} />;
  }

  return <>{children}</>;
}
